<template>
  <div class="flex">
    <!-- Sidebar -->
    <div class="fixed z-30 h-screen bg-white border-r border-gray-200 flex flex-col" :class="[
      isExpanded ? 'w-64' : 'w-20',
      menuStatus ? 'left-0' : '-left-full md:left-0'
    ]" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
      <!-- Logo Area -->
      <div class="flex items-center h-16 px-4 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center gap-3">
          <div class="min-w-[32px]">
            <div v-if="!vendorLogoUrl"
              class="flex items-center justify-center h-8 w-8 rounded-full border border-gray-200 bg-gray-100 text-gray-600 text-sm font-semibold">
              {{ vendorInitial }}
            </div>
           <div class="border border-gray-200 rounded-full w-8 h-8 overflow-hidden flex items-center" v-else>
             <NuxtImg  size="md" width="100"   fit="contain"
              :src="vendorLogoUrl || undefined" :alt="vendorName" />
           </div>
          </div>
          <div class="overflow-hidden transition-all duration-300"
            :class="[isExpanded ? 'w-auto opacity-100' : 'w-0 opacity-0']">
            <p class="text-sm font-medium text-gray-900 leading-tight">{{ vendorName }}</p>
            <p class="text-xs text-gray-500">
              Hoşgeldiniz
            </p>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <nav class="flex-1 px-2 py-4 space-y-1 custom-scrollbar overflow-y-auto max-h-[calc(100vh-8rem)]">
        <div v-for="category in visibleCategories" :key="category.name">
          <!-- Ana Menü -->
          <div class="mb-2">
            <div @click="toggleMenu(category.name)"
              class="flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 cursor-pointer"
              :class="[
                isActiveRoute(category.url)
                  ? 'text-gray-900 bg-gray-50'
                  : 'text-gray-600 hover:bg-gray-50'
              ]">
              <div class="flex items-center">
                <div class="min-w-[24px]">
                  <Icon :name="category.icon" class="w-5 h-5 transition-colors duration-200" :class="[
                    isActiveRoute(category.url)
                      ? 'text-secondary-500'
                      : 'text-gray-500'
                  ]" />
                </div>
                <span class="ml-2 whitespace-nowrap overflow-hidden transition-all duration-300" :class="[
                  isExpanded ? 'w-auto opacity-100 ' : 'w-0 opacity-0 ',
                  isActiveRoute(category.url)
                    ? 'text-secondary-500'
                    : 'text-gray-500'
                ]">
                  {{ category.name }}
                </span>
              </div>

              <!-- Açılır/Kapanır ok ikonu -->
              <Icon v-if="category.children && isExpanded" :name="openMenus.includes(category.name)
                ? 'mdi:chevron-up'
                : 'mdi:chevron-down'
                " class="w-5 h-5 text-gray-400" />
            </div>

            <!-- Alt Menüler -->
            <div v-if="category.children" class="space-y-1 transition-all duration-300" :class="[
              isExpanded ? 'block' : 'hidden',
              openMenus.includes(category.name) ? 'block' : 'hidden'
            ]">
              <NuxtLink v-for="child in category.children" :key="child.url" :to="child.url"
                class="flex items-center transition-colors duration-200" :class="[
                  isActiveRoute(child.url)
                    ? 'text-orange-500'
                    : 'text-gray-500 hover:text-gray-900'
                ]">
                <span class="whitespace-nowrap overflow-hidden transition-all duration-300 pl-11 py-2 pr-3 text-sm"
                  :class="[isExpanded ? 'w-auto opacity-100' : 'w-0 opacity-0']">
                  {{ child.name }}
                </span>
              </NuxtLink>
            </div>
          </div>
          <hr v-if="category.dividerAfter" class="border-gray-200 mx-3 my-3" />
        </div>
      </nav>

      <!-- User Profile & Logout -->
      <div class="flex-shrink-0 border-t border-gray-200">
        <div class="p-4">
          <!-- Logout Button -->
          <UButton color="red" variant="soft" size="sm" class="w-full" @click="handleLogout"
            :class="[!isExpanded && 'px-2']">
            <Icon name="heroicons:arrow-right-on-rectangle" class="w-5 h-5" />
            <span class="whitespace-nowrap overflow-hidden transition-all duration-300"
              :class="[isExpanded ? 'w-auto opacity-100' : 'w-0 opacity-0']">
              Çıkış Yap
            </span>
          </UButton>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 md:ml-20" :class="{ 'md:ml-64': isExpanded }">
      <!-- Top Header -->
      <header
        class="flex items-center justify-between h-14 md:h-16 px-4 md:px-6 bg-white border-b border-gray-200 sticky top-0 z-20">
        <!-- Mobile Menu Button -->
        <UButton class="md:hidden" :icon="menuStatus ? 'i-heroicons-x-mark' : 'i-heroicons-bars-3'"
          @click="menuStatus = !menuStatus" color="gray" variant="soft" />

        <!-- Header Right Items -->
        <div class="flex items-center ml-auto space-x-2 md:space-x-4">
          <!-- Notifications -->
          <UButton icon="i-heroicons-bell" color="gray" variant="soft" />

          <!-- Settings -->
          <UButton icon="i-heroicons-cog-6-tooth" color="gray" variant="soft" />
        </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 bg-gray-50/40">
        <div class="md:px-6 p-6">
          <NuxtPage />
        </div>

        <!-- Footer -->
        <footer class="py-3 md:py-4 text-center text-xs md:text-sm text-gray-600 bg-white border-t border-gray-200">
          <p>
            © {{ new Date().getFullYear() }} Beyraha Admin Panel. Tüm hakları
            saklıdır.
          </p>
        </footer>
      </main>
    </div>

    <!-- Overlay - Mobilde menü açıkken arka planı karartmak için -->
    <div v-if="menuStatus" class="fixed inset-0 bg-black/20 z-20 md:hidden" @click="menuStatus = false"></div>
  </div>
</template>

<script setup>
const { isMobile } = useDevice()
const menuStatus = ref(false)
const isExpanded = ref(isMobile)
const isLoading = ref(true)

// Route kontrolü için composable
const route = useRoute()

// Route değişimlerini izle
watch(
  () => route.fullPath,
  () => {
    isLoading.value = true
    // Sayfa yüklenme simülasyonu (isteğe bağlı)
    setTimeout(() => {
      isLoading.value = false
    }, 500)
  },
  { immediate: true }
)

// Sayfa yüklendiğinde loading'i kapat
onMounted(() => {
  setTimeout(() => {
    isLoading.value = false
  }, 500)
})

// Aktif route kontrolü için yardımcı fonksiyon
const isActiveRoute = (url) => {
  if (!url) return false
  return route.path.includes(url)
}

const auth = useAuth()
const authStore = useAuthStore()
const isAdmin = computed(() => auth.data.value?.user?.role === 'admin')

const { API_ENDPOINTS } = useApiEndpoints()
const vendorInfoState = useState('vendor-info', () => ({
  name: '',
  logo: null
}))

const vendorName = computed(() => {
  if (vendorInfoState.value.name) {
    return vendorInfoState.value.name
  }
  return auth.data.value?.user?.name || 'Mağazam'
})

const vendorInitial = computed(() => {
  return vendorName.value?.trim().charAt(0)?.toUpperCase() || 'M'
})

const vendorLogoUrl = computed(() => {
  const logo = vendorInfoState.value.logo
  if (!logo) return null
  if (typeof logo === 'string') return logo
  if (logo?.url) return logo.url
  if (logo?.path) return `cl/${logo.path}`
  return null
})

const fetchVendorInfo = async () => {
  if (vendorInfoState.value.name) return
  try {
    const response = await useBaseOFetchWithAuth(
      API_ENDPOINTS.protected.vendor.settings.store
    )
    if (response?.data) {
      vendorInfoState.value = {
        name: response.data.name || '',
        logo: response.data.logo || null
      }
    }
  } catch (error) {
    console.error('Failed to load vendor info', error)
  }
}

onMounted(fetchVendorInfo)

const categories = reactive([
  {
    name: 'Genel Görünüm',
    icon: 'mdi:view-dashboard-outline',
    url: '/management'
  },
  {
    name: 'Mağazam',
    icon: 'mdi:store',
    url: '/management/magazam',
    children: [
      {
        name: 'Mağaza Ayarları',
        url: '/management/magazam/ayarlar'
      },
      {
        name: 'Mağaza Adresleri',
        url: '/management/magazam/adresler'
      },
      {
        name: 'Banka Ayarları',
        url: '/management/magazam/ayarlar/banka-ayarlari'
      }
    ]
  },
  {
    name: 'Ürünler',
    icon: 'mdi:package-variant',
    url: '/management/urunler',
    children: [
      {
        name: 'Ürün listesi',
        url: '/management/urunler'
      },
      {
        name: 'Yeni Ürün Ekle',
        url: '/management/urunler/urun-ekle'
      }
    ]
  },
  {
    name: 'Siparişler',
    icon: 'mdi:shopping',
    url: '/management/siparisler',
    children: [
      {
        name: 'Sipariş listesi',
        url: '/management/siparisler'
      }
    ],
    dividerAfter: true
  },

  // Satıcı başvuruları menü girişi
  {
    name: 'Başvurular',
    icon: 'mdi:clipboard-text',
    url: '/management/basvurular',
    requiresAdmin: true,
    children: [
      {
        name: 'Başvurular',
        url: '/management/basvurular'
      }
    ]
  },

  {
    name: 'Karosel Yönetimi',
    icon: 'mdi:view-carousel',
    url: '/management/karoseller',
    requiresAdmin: true,
    children: [
      {
        name: 'Karoseller',
        url: '/management/karoseller'
      }
    ]
  },

  {
    name: 'Ayarlar',
    icon: 'mdi:cog-outline',
    url: '/management/admin/ayarlar',
    requiresAdmin: true,
    children: [
      {
        name: 'Genel Ayarlar',
        url: '/management/admin/ayarlar'
      }
    ]
  },

  {
    name: 'Kategori Yönetimi',
    icon: 'mdi:view-list-outline',
    url: '/management/admin/kategoriler',
    requiresAdmin: true,
    children: [
      {
        name: 'Kategoriler',
        url: '/management/admin/kategoriler'
      }
    ]
  },

  {
    name: 'Nitelik Yönetimi',
    icon: 'mdi:tune',
    url: '/management/admin/nitelikler',
    requiresAdmin: true,
    children: [
      {
        name: 'Nitelikler',
        url: '/management/admin/nitelikler'
      }
    ]
  },

  {
    name: 'Markalar',
    icon: 'mdi:tag-multiple',
    url: '/management/markalar',
    requiresAdmin: true,
    children: [
      {
        name: 'Marka Listesi',
        url: '/management/markalar'
      },
      {
        name: 'Marka Ekle',
        url: '/management/markalar/ekle'
      }
    ]
  },

  {
    name: 'Komisyon Yönetimi',
    icon: 'mdi:cash-multiple',
    url: '/management/admin/komisyon',
    requiresAdmin: true,
    children: [
      {
        name: 'Trendyol Uyarlama',
        url: '/management/admin/komisyon/trendyol-uyarlama'
      }
    ]
  },

  {
    name: 'Kargo İşlemleri',
    icon: 'mdi:truck-outline',
    url: '/management/admin/kargo',
    requiresAdmin: true,
    children: [
      {
        name: 'Kargo Firmaları',
        url: '/management/admin/kargo/kargo-firmalari'
      },
      {
        name: 'Sağlayıcılar',
        url: '/management/admin/kargo/saglayicilar'
      }
    ]
  },

  {
    name: 'Teknik İşlemler',
    icon: 'mdi:wrench-outline',
    url: '/management/admin/teknik-islemler',
    requiresAdmin: true,
    children: [
      {
        name: 'Site Sağlığı',
        url: '/management/admin/teknik-islemler/site-sagligi'
      }
    ]
  },

  {
    name: 'Bölgeler',
    icon: 'mdi:map-outline',
    url: '/management/admin/bolgeler',
    requiresAdmin: true,
    children: [
      {
        name: 'Bölge Listesi',
        url: '/management/admin/bolgeler'
      },
      {
        name: 'Bölge Senkronizasyonu',
        url: '/management/admin/bolgeler/senkronizasyon'
      }
    ]
  },

  {
    name: 'Elasticsearch',
    icon: 'mdi:database-search',
    url: '/management/admin/elasticsearch',
    requiresAdmin: true,
    children: [
      {
        name: 'Manuel İşlemler',
        url: '/management/admin/elasticsearch/manuel-islemler'
      }
    ]
  }
])

const visibleCategories = computed(() =>
  categories.filter((category) => !category.requiresAdmin || isAdmin.value)
)

// Sayfa değişimlerinde mobil menüyü kapat
watch(
  () => useRoute().fullPath,
  () => {
    menuStatus.value = false
  }
)

// ESC tuşuna basıldığında menüyü kapat
onMounted(() => {
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      menuStatus.value = false
    }
  })
})

// Açık olan menü itemlerini tutmak için
const openMenus = ref([])

// Route değişiminde ilgili menüyü otomatik aç
watch(
  () => route.path,
  (newPath) => {
    // Hangi ana menünün altında olduğumuzu bul
    const activeParent = visibleCategories.value.find((category) =>
      category.children?.some((child) => child.url === newPath)
    )
    if (activeParent) {
      openMenus.value = [activeParent.name]
    } else {
      openMenus.value = []
    }
  },
  { immediate: true }
)

watch(
  () => visibleCategories.value,
  (newCategories) => {
    openMenus.value = openMenus.value.filter((name) =>
      newCategories.some((category) => category.name === name)
    )
  },
  { immediate: true }
)

// Menü toggle fonksiyonu
const toggleMenu = (categoryName) => {
  const index = openMenus.value.indexOf(categoryName)
  if (index === -1) {
    openMenus.value.push(categoryName)
  } else {
    openMenus.value.splice(index, 1)
  }
}

// Çıkış işlemi için
const handleLogout = async () => {
  await authStore.logout('/management/login')
}

// Masaüstünde hover ile açılma/kapanma
const HOVER_OPEN_DELAY_MS = 400 // Menü açılmadan önce bekleme süresi (ms)
let hoverTimer = null

const clearHoverTimer = () => {
  if (hoverTimer !== null) {
    clearTimeout(hoverTimer)
    hoverTimer = null
  }
}

const handleMouseEnter = () => {
  if (!isMobile) {
    clearHoverTimer()
    // Masaüstünde menünün ani açılmasını engellemek için kısa gecikme uygular
    hoverTimer = setTimeout(() => {
      isExpanded.value = true
      hoverTimer = null
    }, HOVER_OPEN_DELAY_MS)
  }
}

const handleMouseLeave = () => {
  if (!isMobile) {
    clearHoverTimer()
    isExpanded.value = false
  }
}

onBeforeUnmount(() => {
  clearHoverTimer()
})

// Cihaz tipi değiştiğinde menü durumunu güncelle
// watch(isMobile, (newValue) => {
//   isExpanded.value = newValue
// })
// </script>
